import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { DOCUMENT } from '@angular/common';
import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, AfterViewInit, Output, EventEmitter, Inject } from '@angular/core';
import * as pdfjsLib from 'pdfjs-dist';




@Component({
  selector: 'gsp-pdf-viewer',
  templateUrl: './pdf-viewer.component.html',
  styleUrls: ['./pdf-viewer.component.css']
})
export class PdfViewerComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('pdfContainer') pdfContainer!: ElementRef;
  @ViewChild('pdfViewer') pdfViewer!: ElementRef;
  @Input() pdfUrl: string = '';
  @Output() lastPageEvent = new EventEmitter<boolean>();
  pdfDoc: any = null;
  pageNum: number = 1;
  isLoading: boolean = true;
  private scale: number = 1.5;
  private pageHeights: number[] = [];
  private cumulativeHeights: number[] = [0];
  constructor(
    public breakpointObserver: BreakpointObserver,@Inject(DOCUMENT) private document:Document
  ) { 
    this.initWorkerSrc();
  }

 
  private initWorkerSrc(): void {
    const baseHref = this.document.getElementsByTagName('base')[0]?.getAttribute('href') || '/';
    const normalizedBase = baseHref.endsWith('/') ? baseHref : baseHref + '/';
    const preferred = normalizedBase + 'assets/pdfjs/build/pdf.worker.min.mjs';
    const fallback = '/assets/pdfjs/build/pdf.worker.min.mjs';
    pdfjsLib.GlobalWorkerOptions.workerSrc = preferred;
    this.verifyWorker([preferred, fallback]);
  }

  private async verifyWorker(candidates: string[]): Promise<void> {
    for (const url of candidates) {
      try {
        const resp = await fetch(url, { method: 'HEAD' });
        if (resp.ok) {
          if (pdfjsLib.GlobalWorkerOptions.workerSrc !== url) {
            pdfjsLib.GlobalWorkerOptions.workerSrc = url;
            console.info('[PDF] Using worker:', url);
          }
          return;
        }
      } catch (_) { /* try next */ }
    }
    console.warn('[PDF] Could not verify any worker path. Continuing with:', pdfjsLib.GlobalWorkerOptions.workerSrc);
  }

  ngOnInit(): void {

    // const workerPath = new URL('assets/pdfjs/build/pdf.worker.min.mjs', window.location.origin).href;

    this.breakPointChanged();
    this.loadPDF();
  }

  ngAfterViewInit(): void {
    this.adjustViewerHeight();
    window.addEventListener('resize', this.adjustViewerHeight.bind(this));
  }
  private breakPointChanged() {
    this.breakpointObserver
      .observe(['(min-width: 545px)', '(min-width: 961px)', '(max-width: 768px)'])
      .subscribe((state: BreakpointState) => {
        let breakpoints = state.breakpoints;
        switch (true) {
          case breakpoints['(max-width: 768px)']:
            this.scale = 0.7;
            break;
          default:
            this.scale = 1.5;
        }
      });
  }

  ngOnDestroy(): void {
    if (this.pdfDoc) {
      this.pdfDoc.destroy();
    }
    window.removeEventListener('resize', this.adjustViewerHeight.bind(this));
  }

  private adjustViewerHeight(): void {
    const windowHeight = window.innerHeight;
    const controlsHeight = document.querySelector('.pdf-controls')?.clientHeight || 60;
    const padding = 20;
    const viewerHeight = windowHeight - controlsHeight - padding;
    this.pdfViewer.nativeElement.style.height = `${viewerHeight}px`;
  }

  private async loadPDF(): Promise<void> {
    try {
      this.isLoading = true;
      const loadingTask = pdfjsLib.getDocument(this.pdfUrl);
      this.pdfDoc = await loadingTask.promise;
      await this.renderAllPagesInOrder();
    } catch (error) {
      console.error('PDF loading error:', error);
    } finally {
      this.isLoading = false;
    }
  }

  private async renderAllPagesInOrder(): Promise<void> {
    this.pdfContainer.nativeElement.innerHTML = '';
    this.pageHeights = [];
    this.cumulativeHeights = [0];

    // Create placeholder elements first to maintain order
    const placeholders: HTMLElement[] = [];
    for (let i = 1; i <= this.pdfDoc.numPages; i++) {
      const placeholder = document.createElement('div');
      placeholder.id = `page-${i}-placeholder`;
      placeholder.style.minHeight = '100px'; // Temporary height
      placeholder.className = 'page-placeholder';
      this.pdfContainer.nativeElement.appendChild(placeholder);
      placeholders.push(placeholder);
    }

    // Render pages sequentially but don't wait between pages
    for (let i = 1; i <= this.pdfDoc.numPages; i++) {
      await this.renderPage(i, placeholders[i - 1]);
    }

    // Set container height after all pages are rendered
    const totalHeight = this.cumulativeHeights[this.cumulativeHeights.length - 1];
    this.pdfContainer.nativeElement.style.height = `${totalHeight}px`;
  }

  private async renderPage(pageNumber: number, placeholder: HTMLElement): Promise<void> {
    try {
      const page = await this.pdfDoc.getPage(pageNumber);
      const viewport = page.getViewport({ scale: this.scale });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      canvas.className = 'pdf-page-canvas';
      this.pageHeights[pageNumber - 1] = viewport.height;
      this.cumulativeHeights[pageNumber] = this.cumulativeHeights[pageNumber - 1] + viewport.height;
      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;
      placeholder.replaceWith(canvas);
      if (pageNumber < this.pdfDoc.numPages) {
        const separator = document.createElement('div');
        separator.className = 'page-separator';
        this.pdfContainer.nativeElement.insertBefore(separator, canvas.nextSibling);
      }
    } catch (error) {
      console.error(`Error rendering page ${pageNumber}:`, error);
    }
  }

  handleScroll(event: Event): void {
    if (!this.pdfDoc || this.isLoading) return;
    const scrollTop = this.pdfViewer.nativeElement.scrollTop;
    const containerHeight = this.pdfViewer.nativeElement.clientHeight;
    for (let i = 1; i <= this.pdfDoc.numPages; i++) {
      if (scrollTop < this.cumulativeHeights[i] - (containerHeight * 0.3)) {
        this.pageNum = i;
        if (this.pageNum == this.pdfDoc.numPages) {
          this.lastPageEvent.emit(true);
        }
        else {
          this.lastPageEvent.emit(false);
        }
        break;
      }
    }
  }

  previousPage(): void {
    if (this.pageNum <= 1 || this.isLoading) return;
    this.pageNum--;
    this.scrollToPage(this.pageNum);
  }

  nextPage(): void {
    if (this.pageNum >= this.pdfDoc.numPages || this.isLoading) return;
    this.pageNum++;
    this.scrollToPage(this.pageNum);
  }

  scrollToPage(pageNum: number): void {
    if (this.isLoading) return;
    const offset = 10;
    this.pdfViewer.nativeElement.scrollTop = this.cumulativeHeights[pageNum - 1] + offset;
  }

  scrollToBottom(): void {
    if (this.isLoading) return;
    this.pdfViewer.nativeElement.scrollTop = this.pdfContainer.nativeElement.scrollHeight;
    this.pageNum = this.pdfDoc.numPages;
  }
}